import React from "react";
import { IoIosHelpCircleOutline } from "react-icons/io";
import { FormattedMessage } from "react-intl";
import { IconWrapper } from "../utils/IconWrapper";
import { formatLink } from "../../utils/functions";
import { AiFillCaretDown } from "react-icons/ai";

interface FormSelectProps {
  id: string;
  options?: string[];
  dictionaryOptions?: { [key: string]: string };
  question?: string;
  type?: string;
  itemAttribute?: string;
  value?: string;
  divider?: string;
  containerStyle?: string;
  inputStyle?: string;
  divStyle?: string;
  labelStyle?: string;
  repeatable?: boolean;
  dividerHeaderStyle?: string;
  register?: any;
  errors?: Record<string, Object>;
  link?: string | boolean;
  cursiveLabel?: string;
  repeatableHeader?: string | boolean;
  repeatableKey?: { key: string; last: boolean; amount: number };
  required?: boolean;
  overlayId?: string;
  index?: number;
  disabled?: boolean;
  changeAmount?: (key: string, direction: number) => void;
  overlayToggle?: (id: string) => void;
  validate?: (id: string) => void;
  handleInputChange: (
    event: React.ChangeEvent<HTMLSelectElement>,
    id: string,
    type?: string
  ) => void;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  id,
  options,
  handleInputChange,
  question,
  value,
  containerStyle,
  divider,
  errors,
  itemAttribute,
  repeatableHeader,
  required,
  dictionaryOptions,
  validate,
  disabled,
  inputStyle,
  divStyle,
  labelStyle,
  dividerHeaderStyle,
  repeatable,
  register,
  link,
  cursiveLabel,
  repeatableKey,
  changeAmount,
  overlayToggle,
  overlayId,
  index,
}) => {
  const styles = inputStyle
    ? inputStyle
    : "border border-gray-300 rounded-lg h-10 m-4 leading-normal cursor-pointer";

  const stylesInner = inputStyle
    ? inputStyle
    : "font-semibold text-brand-dark_blue focus:outline-none focus:bg-blue-100 focus:shadow-inner h-full w-full rounded-lg pl-5  leading-normal cursor-pointer";

  const divStyles = divStyle
    ? divStyle
    : "flex flex-col mb-4 xl:w-11/12 xl:m-auto";

  const labelStyles = labelStyle
    ? labelStyle
    : "m-2 uppercase font-bold text-lg text-brand-dark_blue";

  const linkStyles =
    "hover:text-brand-dark_blue text-brand-blue mb-2 uppercase font-bold text-lg";

  const participantId = repeatable ? id.split("_")[1] : ""; //expected id is 'BLABLABLA_2' => 2

  const dividerHeaderStyles = dividerHeaderStyle
    ? dividerHeaderStyle
    : "m-2 ml-4 uppercase font-bold text-lg text-brand-dark_blue";

  const dividerStyles = divider ? divider : "";
  const containerStyles = containerStyle ? containerStyle : "";

  const opts = dictionaryOptions ? Object.keys(dictionaryOptions) : options;

  const errorDiv = errors ? (
    errors[id] ? (
      <p className='text-red-400 ml-4'>
        {" "}
        <FormattedMessage id={errors[id]["message"]} />
      </p>
    ) : null
  ) : null;

  const requiredAsterisk = required ? (
    <span className='font-normal text-red-500'>*</span>
  ) : null;

  const mouseHoverTrigger = (e: any) => {
    overlayToggle(overlayId);
  };

  const helpIcon = overlayId ? (
    <IconWrapper styles={"self-center"}>
      <IoIosHelpCircleOutline
        onMouseEnter={(e) => mouseHoverTrigger(e)}
        onMouseLeave={(e) => mouseHoverTrigger(e)}
        size={30}
      ></IoIosHelpCircleOutline>
    </IconWrapper>
  ) : null;

  const labelTag =
    typeof link === "string" ? (
      <span className={`${linkStyles}`}>
        <a href={formatLink(link)} target='_blank' rel='noreferrer'>
          {question}
          {requiredAsterisk}
        </a>
      </span>
    ) : question && overlayId ? (
      <div className='flex relative'>
        <span className={`${labelStyles}`}>
          {question}
          {requiredAsterisk}
        </span>
        {helpIcon}
      </div>
    ) : question ? (
      <span className={`${labelStyles}`}>
        {question}
        {requiredAsterisk}
      </span>
    ) : null;

  const additionalLabel = cursiveLabel ? (
    <p className='text-brand-dark_blue italic ml-4'>{cursiveLabel}</p>
  ) : null;
  const buttonStyle =
    "bg-blue-400 hover:bg-brand-blue text-brand-white py-2 px-4 rounded m-5 w-auto";
  const hideDecreaseRepeatableStyle = repeatableKey
    ? Number(repeatableKey.amount) === 1
      ? "invisible focus:outline-none outline-none"
      : "visible"
    : null;
  const lastOfRepeatGroup = repeatableKey
    ? repeatableKey.last
      ? true
      : false
    : false;
  const repeatableButtons = lastOfRepeatGroup ? (
    <div className='flex flex-row justify-between'>
      <button
        onClick={() => changeAmount(repeatableKey.key, 1)}
        type='button'
        className={`${buttonStyle}`}
      >
        <FormattedMessage id='Form.Repeat_add' />
      </button>
      <button
        onClick={() => changeAmount(repeatableKey.key, -1)}
        type='button'
        className={`${buttonStyle} ${hideDecreaseRepeatableStyle}`}
      >
        <FormattedMessage id='Form.Repeat_remove' />
      </button>
    </div>
  ) : null;

  return (
    <div
      className={`relative ${divStyles} ${containerStyles} ${dividerStyles}`}
      tabIndex={index}
    >
      {divider && repeatable ? (
        <span className={`${dividerHeaderStyles}`}>
          {repeatableHeader} {participantId}
        </span>
      ) : null}
      {labelTag}
      {additionalLabel}
      <div className={`relative ${styles}`}>
        <select
          className={`appearance-none ${stylesInner} ${
            disabled ? "bg-gray-200" : ""
          }`}
          id={id}
          value={value}
          onChange={(e) => handleInputChange(e, id, itemAttribute)}
          name={id}
          ref={register}
          onBlur={validate ? () => validate(id) : null}
          disabled={disabled ? true : false}
        >
          {opts?.map((option) => (
            <option
              key={option}
              value={dictionaryOptions ? dictionaryOptions[option] : option}
            >
              {option}
            </option>
          ))}
        </select>
        <IconWrapper styles='text-brand-dark_blue absolute right-0 mr-5 mt-2'>
          <AiFillCaretDown size={20} />
        </IconWrapper>
      </div>
      {errorDiv}
      {repeatableButtons}
    </div>
  );
};

export default FormSelect;
